import { useEffect } from "react";
import { useParams } from "react-router-dom";
import * as zoom from "@/zoom";
import { getZoomSignature, ZoomSignature } from "@/api/getZoomSignature";
import { useSharedWorkerState } from "@/hooks/useSharedWorkerState";

type SendSharedStateMessage = ReturnType<typeof useSharedWorkerState>[1];

function pollForBreakoutRoomInfo(
  sendSharedStateMessage: SendSharedStateMessage,
) {
  const pollInterval = 500; // 0.5 seconds
  let breakoutRoomId = "";

  async function getBreakoutRoomInfo() {
    try {
      const breakoutRoomData = await zoom.getBreakoutRoomInfo();

      if (!breakoutRoomData.status && breakoutRoomData.errorMessage) {
        new Error(breakoutRoomData.errorMessage);
      }

      // Handle setting the current breakout room
      if (
        breakoutRoomData.result.roomId !== breakoutRoomId &&
        (breakoutRoomData.result.attendeeStatus === "in room" ||
          breakoutRoomData.result.attendeeStatus === "return main session")
      ) {
        breakoutRoomId = breakoutRoomData.result.roomId;
        sendSharedStateMessage.setBreakoutRoomInfo(
          breakoutRoomData.result.roomId,
          breakoutRoomData.result.name,
        );
      }

      // Handle auto joining a breakout room
      if (breakoutRoomData.result.attendeeStatus === "be invited") {
        await zoom.joinBreakoutRoom(breakoutRoomData.result.roomId);
      }
    } catch (error) {
      console.error(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  setInterval(getBreakoutRoomInfo, pollInterval);
}

function pollForParticipantInfo(
  sendSharedStateMessage: SendSharedStateMessage,
) {
  const pollInterval = 5000; // 5 seconds

  async function getParticipantInfo() {
    try {
      const data = await zoom.getParticipants();

      if (!data.status && data.errorMessage) {
        new Error(data.errorMessage);
      }

      sendSharedStateMessage.updateParticipantList(data.result.attendeesList);
    } catch (error) {
      console.error(error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  setInterval(getParticipantInfo, pollInterval);
}

function listenToCaptions(sendSharedStateMessage: SendSharedStateMessage) {
  zoom.listenForCaptions((data) => {
    sendSharedStateMessage.addCaption(data);
  });
}

async function startMeeting(
  joinInfo: ZoomSignature,
  sendSharedStateMessage: SendSharedStateMessage,
) {
  sendSharedStateMessage.setUserInfo(joinInfo.displayName, joinInfo.email);

  await zoom.init(`/thank-you`);
  await zoom.joinMeeting(
    import.meta.env.VITE_ZOOM_SDK_KEY,
    joinInfo.meetingId,
    joinInfo.signature,
    joinInfo.displayName,
    joinInfo.email,
    joinInfo.tk,
    joinInfo.zak,
    joinInfo.passcode,
  );
  const meetingInfo = await zoom.getCurrentMeetingInfo();

  if (!meetingInfo.status && meetingInfo.errorMessage) {
    new Error(meetingInfo.errorMessage);
  }

  sendSharedStateMessage.setMeetingInfo(
    meetingInfo.result.meetingNumber.toString(),
    meetingInfo.result.meetingTopic,
    meetingInfo.result.mid,
  );
  pollForBreakoutRoomInfo(sendSharedStateMessage);
  pollForParticipantInfo(sendSharedStateMessage);
  listenToCaptions(sendSharedStateMessage);
}

function Frame() {
  const { shortId } = useParams();
  const [, sendSharedStateMessage] = useSharedWorkerState();

  // Prepare the Zoom Meeting SDK once on component mount
  useEffect(() => {
    zoom.preLoadWasm();
    zoom.prepareWebSDK();
  }, []);

  // Fetch the join info for the meeting when the shortId changes
  useEffect(() => {
    async function getJoinInfo() {
      if (shortId) {
        try {
          const result = await getZoomSignature(shortId);
          if (result.jwt) {
            sendSharedStateMessage.setToken(result.jwt);
            await startMeeting(result, sendSharedStateMessage);
          }
        } catch (err) {
          console.error(err);
        }
      }
    }

    void getJoinInfo();
  }, [shortId]);

  return null;
}

export default Frame;
